import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  TrendingUp,
  Clock,
  Stethoscope,
  CheckCircle2,
  Target,
  BookOpen,
  Users,
  FileText,
  Phone,
  Video,
  ArrowRight,
  Star,
  Shield,
  Lightbulb,
  Activity,
  Heart,
  MessageSquare
} from "lucide-react";

// Training modules focused on lower-performing CAHPS measures
const TRAINING_MODULES = {
  timely_help: {
    title: "Improving Timely Response",
    measure: "Getting Timely Help",
    icon: Clock,
    color: "text-orange-600 bg-orange-100",
    modules: [
      {
        id: "th-1",
        title: "Rapid Response Protocol",
        duration: "15 min",
        type: "video",
        description: "Learn our updated rapid response workflow for urgent patient needs",
        objectives: [
          "Identify urgent vs. routine requests",
          "Escalation pathways for after-hours calls",
          "Documentation requirements for response times"
        ]
      },
      {
        id: "th-2",
        title: "Triage Communication Skills",
        duration: "20 min",
        type: "interactive",
        description: "Practice scenarios for effective phone triage and response",
        objectives: [
          "Active listening techniques",
          "Asking the right questions quickly",
          "Setting realistic expectations with families"
        ]
      },
      {
        id: "th-3",
        title: "On-Call Best Practices",
        duration: "10 min",
        type: "document",
        description: "Guidelines for on-call staff to ensure timely patient care",
        objectives: [
          "Pre-shift preparation checklist",
          "Resource availability confirmation",
          "Handoff communication standards"
        ]
      }
    ],
    workflows: [
      {
        title: "30-Minute Response Standard",
        description: "All urgent calls receive callback within 30 minutes",
        steps: [
          "Call received and logged in system",
          "Triage nurse assesses urgency (5 min)",
          "Appropriate clinician notified (5 min)",
          "Patient/family callback initiated (20 min max)",
          "Response documented with timestamp"
        ]
      },
      {
        title: "Visit Request Optimization",
        description: "Streamlined process for urgent home visits",
        steps: [
          "Visit request received via any channel",
          "On-call coordinator reviews schedule",
          "Nearest available nurse/aide assigned",
          "ETA communicated to family",
          "Visit completed and documented same day"
        ]
      }
    ],
    tips: [
      "Always acknowledge the call/message immediately, even if full response takes longer",
      "Use automated text confirmations: 'We received your message and will respond within 30 minutes'",
      "Maintain updated on-call schedules accessible to all team members",
      "Track response times weekly and address patterns in team meetings"
    ]
  },
  pain: {
    title: "Pain & Symptom Management Excellence",
    measure: "Help for Pain & Symptoms",
    icon: Stethoscope,
    color: "text-red-600 bg-red-100",
    modules: [
      {
        id: "ps-1",
        title: "Pain Assessment Fundamentals",
        duration: "25 min",
        type: "video",
        description: "Comprehensive pain assessment techniques for hospice patients",
        objectives: [
          "Use validated pain scales effectively",
          "Assess pain in non-verbal patients",
          "Document pain levels accurately"
        ]
      },
      {
        id: "ps-2",
        title: "Breakthrough Pain Management",
        duration: "20 min",
        type: "interactive",
        description: "Protocols for managing breakthrough pain episodes",
        objectives: [
          "Recognize breakthrough pain patterns",
          "PRN medication administration guidelines",
          "When to escalate to physician"
        ]
      },
      {
        id: "ps-3",
        title: "Non-Pharmacological Comfort Measures",
        duration: "15 min",
        type: "video",
        description: "Complementary approaches to pain and symptom relief",
        objectives: [
          "Positioning and repositioning techniques",
          "Heat/cold therapy applications",
          "Relaxation and distraction methods"
        ]
      },
      {
        id: "ps-4",
        title: "Family Education on Pain Management",
        duration: "15 min",
        type: "document",
        description: "Teaching families to participate in pain management",
        objectives: [
          "Medication administration training",
          "Signs to watch for and report",
          "Comfort measure techniques for caregivers"
        ]
      }
    ],
    workflows: [
      {
        title: "Pain Reassessment Protocol",
        description: "Systematic approach to ongoing pain management",
        steps: [
          "Initial pain assessment on every visit",
          "Document current pain level (0-10 scale)",
          "Compare to previous assessment",
          "Evaluate effectiveness of current regimen",
          "Adjust plan or escalate as needed",
          "Follow-up call within 24 hours if changes made"
        ]
      },
      {
        title: "Symptom Crisis Response",
        description: "Emergency protocol for uncontrolled symptoms",
        steps: [
          "Family calls with symptom crisis",
          "Nurse triages severity immediately",
          "If severe: dispatch nurse within 2 hours",
          "Physician notified for medication orders",
          "Crisis kit medications administered if available",
          "Follow-up visit scheduled within 24 hours"
        ]
      }
    ],
    tips: [
      "Always ask about pain proactively - many patients underreport",
      "Educate families that controlling pain is a primary hospice goal",
      "Ensure comfort kits are stocked and families know how to use them",
      "Document pain assessments at the beginning AND end of each visit"
    ]
  }
};

// Action items for improvement
const ACTION_ITEMS = [
  {
    id: 1,
    category: "timely_help",
    title: "Implement 30-minute callback standard",
    priority: "high",
    status: "in_progress",
    dueDate: "2025-01-31",
    assignee: "Clinical Director",
    description: "Establish and monitor 30-minute callback standard for all urgent calls"
  },
  {
    id: 2,
    category: "timely_help",
    title: "Deploy automated acknowledgment texts",
    priority: "high",
    status: "pending",
    dueDate: "2025-02-15",
    assignee: "IT Team",
    description: "Set up automated text messages to acknowledge patient/family communications"
  },
  {
    id: 3,
    category: "pain",
    title: "Pain assessment training - all clinical staff",
    priority: "high",
    status: "in_progress",
    dueDate: "2025-02-01",
    assignee: "Education Coordinator",
    description: "Complete pain assessment training for all nurses and aides"
  },
  {
    id: 4,
    category: "pain",
    title: "Update comfort kit protocols",
    priority: "medium",
    status: "pending",
    dueDate: "2025-02-28",
    assignee: "Pharmacy Liaison",
    description: "Review and update comfort kit medications and family education materials"
  },
  {
    id: 5,
    category: "pain",
    title: "Implement symptom tracking dashboard",
    priority: "medium",
    status: "pending",
    dueDate: "2025-03-15",
    assignee: "QI Committee",
    description: "Create real-time dashboard for tracking patient symptom levels across census"
  }
];

export default function QualityImprovement() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [cahpsData, setCahpsData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [completedModules, setCompletedModules] = useState([]);

  const allowedRoles = ["staff", "administrator", "admin"];
  const userRole = user?.role?.toLowerCase() || "";
  const isAuthorized = user && allowedRoles.includes(userRole);

  useEffect(() => {
    // Don't do anything while auth is loading
    if (authLoading) return;
    
    // Redirect if not authorized after auth is loaded
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }

    // Fetch CAHPS data
    api.get("/cahps-scores")
      .then(res => {
        setCahpsData(res.data);
        setPageLoading(false);
      })
      .catch(() => setPageLoading(false));

    // Load completed modules from localStorage
    const saved = localStorage.getItem("completedQIModules");
    if (saved) setCompletedModules(JSON.parse(saved));
  }, [user, navigate, authLoading]);

  const markModuleComplete = (moduleId) => {
    const updated = [...completedModules, moduleId];
    setCompletedModules(updated);
    localStorage.setItem("completedQIModules", JSON.stringify(updated));
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-700",
      medium: "bg-yellow-100 text-yellow-700",
      low: "bg-green-100 text-green-700"
    };
    return <Badge className={styles[priority]}>{priority.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-gray-100 text-gray-700",
      in_progress: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700"
    };
    const labels = {
      pending: "Pending",
      in_progress: "In Progress",
      completed: "Completed"
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  // Find the measures that need improvement
  const lowScoreMeasures = cahpsData?.measures?.filter(m => 
    ["timely_help", "pain"].includes(m.id)
  ) || [];

  // Show loading while auth is being checked or data is loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authorized, show nothing (redirect will handle it)
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Access restricted to staff and administrators.</p>
          <Button className="mt-4" onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }
  
  // Show loading while fetching CAHPS data
  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="quality-improvement-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Target className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Quality Improvement Center</h1>
              <p className="text-muted-foreground">CAHPS Score Improvement Resources</p>
            </div>
          </div>

          {/* Score Summary Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {lowScoreMeasures.map((measure) => {
              const isLow = measure.score < 75;
              return (
                <Card key={measure.id} className={`border-2 ${isLow ? 'border-orange-200 bg-orange-50/50' : 'border-green-200 bg-green-50/50'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{measure.name}</span>
                      {isLow && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                    </div>
                    <div className="flex items-end gap-2">
                      <span className={`text-3xl font-bold ${isLow ? 'text-orange-600' : 'text-green-600'}`}>
                        {measure.score}%
                      </span>
                      <span className="text-xs text-muted-foreground pb-1">current</span>
                    </div>
                    <Progress value={measure.score} className="mt-2 h-2" />
                    {isLow && (
                      <p className="text-xs text-orange-600 mt-2">Below national average (~75%)</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Overall Star Rating */}
            <Card className="border-2 border-primary/20 bg-primary/5">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Overall Star Rating</span>
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      className={`w-6 h-6 ${star <= (cahpsData?.star_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-2">Target: 4+ Stars</p>
              </CardContent>
            </Card>

            {/* Training Progress */}
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Training Progress</span>
                  <BookOpen className="w-4 h-4 text-blue-500" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-blue-600">
                    {completedModules.length}
                  </span>
                  <span className="text-xs text-muted-foreground pb-1">
                    / 7 modules
                  </span>
                </div>
                <Progress value={(completedModules.length / 7) * 100} className="mt-2 h-2" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="timely">Timely Help</TabsTrigger>
            <TabsTrigger value="pain">Pain Management</TabsTrigger>
            <TabsTrigger value="actions">Action Items</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Focus Areas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Priority Focus Areas
                  </CardTitle>
                  <CardDescription>
                    Based on your CAHPS scores, these areas need the most attention
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(TRAINING_MODULES).map(([key, module]) => {
                    const Icon = module.icon;
                    const score = cahpsData?.measures?.find(m => m.id === key)?.score || 0;
                    return (
                      <div 
                        key={key}
                        className="flex items-center gap-4 p-4 rounded-xl border hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => setActiveTab(key === "timely_help" ? "timely" : "pain")}
                      >
                        <div className={`w-12 h-12 rounded-xl ${module.color} flex items-center justify-center`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{module.title}</h4>
                          <p className="text-sm text-muted-foreground">{module.modules.length} training modules</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-2xl font-bold ${score < 75 ? 'text-orange-600' : 'text-green-600'}`}>
                            {score}%
                          </span>
                          <p className="text-xs text-muted-foreground">Current Score</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Improvement Metrics
                  </CardTitle>
                  <CardDescription>
                    Track your progress toward quality goals
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Staff Training Completion</span>
                      <span className="font-medium">{Math.round((completedModules.length / 7) * 100)}%</span>
                    </div>
                    <Progress value={(completedModules.length / 7) * 100} className="h-3" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Action Items Completed</span>
                      <span className="font-medium">
                        {ACTION_ITEMS.filter(a => a.status === "completed").length}/{ACTION_ITEMS.length}
                      </span>
                    </div>
                    <Progress 
                      value={(ACTION_ITEMS.filter(a => a.status === "completed").length / ACTION_ITEMS.length) * 100} 
                      className="h-3" 
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-3">Target Goals</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Getting Timely Help</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">68%</span>
                          <ArrowRight className="w-4 h-4" />
                          <span className="text-sm font-medium text-green-600">80%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Pain & Symptoms</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">70%</span>
                          <ArrowRight className="w-4 h-4" />
                          <span className="text-sm font-medium text-green-600">82%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="text-sm">Star Rating</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">3 stars</span>
                          <ArrowRight className="w-4 h-4" />
                          <span className="text-sm font-medium text-green-600">4 stars</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Quick Improvement Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[...TRAINING_MODULES.timely_help.tips.slice(0, 2), ...TRAINING_MODULES.pain.tips.slice(0, 2)].map((tip, index) => (
                    <div key={index} className="flex gap-3 p-3 rounded-lg bg-muted/50">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">{tip}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timely Help Tab */}
          <TabsContent value="timely" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${TRAINING_MODULES.timely_help.color} flex items-center justify-center`}>
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle>{TRAINING_MODULES.timely_help.title}</CardTitle>
                    <CardDescription>Current Score: 68% | Target: 80%</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Training Modules */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Training Modules
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {TRAINING_MODULES.timely_help.modules.map((module) => {
                      const isComplete = completedModules.includes(module.id);
                      return (
                        <Card key={module.id} className={`${isComplete ? 'border-green-200 bg-green-50/30' : ''}`}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-2">
                              <Badge variant="outline" className="text-xs">
                                {module.type === "video" ? <Video className="w-3 h-3 mr-1" /> : 
                                 module.type === "document" ? <FileText className="w-3 h-3 mr-1" /> :
                                 <Users className="w-3 h-3 mr-1" />}
                                {module.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">{module.duration}</span>
                            </div>
                            <h4 className="font-medium mb-2">{module.title}</h4>
                            <p className="text-sm text-muted-foreground mb-3">{module.description}</p>
                            <ul className="text-xs space-y-1 mb-4">
                              {module.objectives.map((obj, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                                  {obj}
                                </li>
                              ))}
                            </ul>
                            <Button 
                              size="sm" 
                              className="w-full"
                              variant={isComplete ? "outline" : "default"}
                              onClick={() => !isComplete && markModuleComplete(module.id)}
                              disabled={isComplete}
                            >
                              {isComplete ? (
                                <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</>
                              ) : (
                                "Start Module"
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                {/* Workflows */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Recommended Workflows
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {TRAINING_MODULES.timely_help.workflows.map((workflow, index) => (
                      <Card key={index}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">{workflow.title}</CardTitle>
                          <CardDescription>{workflow.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {workflow.steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center flex-shrink-0">
                                  {i + 1}
                                </span>
                                <span className="text-sm">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Pro Tips
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {TRAINING_MODULES.timely_help.tips.map((tip, index) => (
                      <div key={index} className="flex gap-3 p-4 rounded-lg border bg-yellow-50/50">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pain Management Tab */}
          <TabsContent value="pain" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${TRAINING_MODULES.pain.color} flex items-center justify-center`}>
                    <Stethoscope className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle>{TRAINING_MODULES.pain.title}</CardTitle>
                    <CardDescription>Current Score: 70% | Target: 82%</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Training Modules */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Training Modules
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {TRAINING_MODULES.pain.modules.map((module) => {
                      const isComplete = completedModules.includes(module.id);
                      return (
                        <Card key={module.id} className={`${isComplete ? 'border-green-200 bg-green-50/30' : ''}`}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-2">
                              <Badge variant="outline" className="text-xs">
                                {module.type === "video" ? <Video className="w-3 h-3 mr-1" /> : 
                                 module.type === "document" ? <FileText className="w-3 h-3 mr-1" /> :
                                 <Users className="w-3 h-3 mr-1" />}
                                {module.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">{module.duration}</span>
                            </div>
                            <h4 className="font-medium mb-2">{module.title}</h4>
                            <p className="text-sm text-muted-foreground mb-3">{module.description}</p>
                            <ul className="text-xs space-y-1 mb-4">
                              {module.objectives.map((obj, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                                  {obj}
                                </li>
                              ))}
                            </ul>
                            <Button 
                              size="sm" 
                              className="w-full"
                              variant={isComplete ? "outline" : "default"}
                              onClick={() => !isComplete && markModuleComplete(module.id)}
                              disabled={isComplete}
                            >
                              {isComplete ? (
                                <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</>
                              ) : (
                                "Start Module"
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                {/* Workflows */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Recommended Workflows
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {TRAINING_MODULES.pain.workflows.map((workflow, index) => (
                      <Card key={index}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">{workflow.title}</CardTitle>
                          <CardDescription>{workflow.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-2">
                            {workflow.steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center flex-shrink-0">
                                  {i + 1}
                                </span>
                                <span className="text-sm">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Pro Tips
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {TRAINING_MODULES.pain.tips.map((tip, index) => (
                      <div key={index} className="flex gap-3 p-4 rounded-lg border bg-red-50/50">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Items Tab */}
          <TabsContent value="actions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Quality Improvement Action Items
                </CardTitle>
                <CardDescription>
                  Track and manage improvement initiatives to boost CAHPS scores
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ACTION_ITEMS.map((item) => (
                    <div 
                      key={item.id}
                      className="flex items-start gap-4 p-4 rounded-xl border hover:bg-muted/30 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        item.category === "timely_help" ? "bg-orange-100" : "bg-red-100"
                      }`}>
                        {item.category === "timely_help" ? (
                          <Clock className="w-5 h-5 text-orange-600" />
                        ) : (
                          <Stethoscope className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{item.title}</h4>
                          {getPriorityBadge(item.priority)}
                          {getStatusBadge(item.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {item.assignee}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Due: {item.dueDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
